import { useQuery } from '@tanstack/react-query'
import { Download, Inbox } from 'lucide-react'
import { useState } from 'react'

import { api } from '../../api/client'
import type { ProjectAsset } from '../../api/types.gen'
import { formatBytes } from '../../lib/format'
import { Badge, Banner, Card, ICON_STROKE, Skeleton } from '../../ui'

/*
 | Es el mismo asset del panel que usa el selector de portada, no otra forma:
 | el endpoint es el mismo con `received=1`. Antes se declaraba aquí a mano y
 | se dejaba fuera la mitad de los campos.
 */
type ReceivedAsset = ProjectAsset

/**
 * Lo que los clientes han mandado por enlaces de recepción.
 *
 * Sección aparte y no mezclado con lo que subió el estudio, por la misma razón
 * que el endpoint las separa: son dos cosas distintas. Una es lo que el estudio
 * entrega; la otra es material que le han enviado y que todavía no ha mirado.
 *
 * Se pinta sólo si hay algo. Un bloque vacío diciendo «no has recibido nada»
 * en la pantalla de todos los proyectos del producto sería un recordatorio
 * permanente de una función que la mayoría de estudios no usa.
 */
export function ReceivedFiles({ projectId }: { projectId: string }) {
  const [abriendo, setAbriendo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['project-received', projectId],
    queryFn: () =>
      api.get<{ data: ReceivedAsset[] }>(`/projects/${projectId}/assets?received=1&limit=60`),
    enabled: projectId !== '',
  })

  /*
   | La descarga se pide y se abre; no navegamos nosotros a la URL firmada.
   |
   | `window.open` con el resultado deja al navegador hacer lo suyo con el
   | Content-Disposition, que es quien restituye el nombre bonito del archivo.
   */
  async function abrir(assetId: string): Promise<void> {
    setAbriendo(assetId)
    setError(null)

    try {
      const respuesta = await api.get<{ data: { url: string } }>(
        `/projects/${projectId}/assets/${assetId}/download`,
      )
      window.open(respuesta.data.url, '_blank', 'noopener')
    } catch {
      setError('No se pudo abrir el archivo. Vuelve a intentarlo.')
    } finally {
      setAbriendo(null)
    }
  }

  if (isPending) return <Skeleton className="h-24 w-full rounded-card" />

  const files = data?.data ?? []

  if (files.length === 0) return null

  const total = files.reduce((sum, file) => sum + file.size_bytes, 0)

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-subhead font-semibold text-ink">
          <Inbox size={18} strokeWidth={ICON_STROKE} aria-hidden />
          Te han enviado
        </h2>
        <span className="text-label text-ink-muted" data-numeric>
          {files.length} {files.length === 1 ? 'archivo' : 'archivos'} · {formatBytes(total)}
        </span>
      </div>

      {error !== null && (
        <div className="mt-3">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      <ul className="mt-4 flex flex-col divide-y divide-line">
        {files.map((file) => (
          <li key={file.id} className="first:pt-0 last:pb-0">
            {/*
              Un botón y no un <a href>: la URL se pide al pulsar, no al pintar
              la lista. Firmarlas todas por adelantado gastaría una firma por
              archivo que casi nunca se usa, y caducarían mientras la pantalla
              está abierta.
            */}
            <button
              type="button"
              onClick={() => void abrir(file.id)}
              disabled={abriendo === file.id}
              className="action flex w-full items-center gap-3 py-2.5 text-left disabled:opacity-60"
            >
            {/* Miniatura si el pipeline pudo hacerla —una foto, un PDF—; si no,
                un hueco del mismo tamaño para que la lista no baile. */}
            <div className="size-10 shrink-0 overflow-hidden rounded bg-surface-sunken">
              {file.thumb_url !== null && (
                <img src={file.thumb_url} alt="" loading="lazy" className="size-full object-cover" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-body text-ink">{file.filename}</p>
              <p className="text-label text-ink-subtle" data-numeric>
                {formatBytes(file.size_bytes)}
                {file.received_via !== null && <> · por «{file.received_via}»</>}
              </p>
            </div>

              {file.kind !== 'image' && <Badge tone="neutral">{file.kind}</Badge>}

              <Download
                size={16}
                strokeWidth={ICON_STROKE}
                aria-hidden
                className="shrink-0 text-ink-subtle"
              />
              <span className="sr-only">Descargar {file.filename}</span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  )
}
